﻿import React, { useState } from 'react';
import { Box, Typography, Button, Input, Alert, CircularProgress } from '@mui/material';
import { uploadBulkData } from '../../services/apiService';
import { toast } from 'react-toastify';

const BulkUpload = () => {
    const [imageFiles, setImageFiles] = useState(null);
    const [metadataFiles, setMetadataFiles] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleImageChange = (event) => {
        setError('');
        setSuccess('');
        setImageFiles(event.target.files);
    };

    const handleMetadataChange = (event) => {
        setError('');
        setSuccess('');
        setMetadataFiles(event.target.files);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!imageFiles || !metadataFiles || imageFiles.length === 0 || metadataFiles.length === 0) {
            setError('Please select both image and metadata files.');
            return;
        }
        // Basic check: Ensure same number of files (more robust matching needed on backend)
        if (imageFiles.length !== metadataFiles.length) {
            setError(`Mismatch: ${imageFiles.length} images and ${metadataFiles.length} metadata files. Please provide matching pairs.`); // Escaped: ${...} twice
             return;
        }

        setError('');
        setSuccess('');
        setIsLoading(true);

        // Create FormData object
        const formData = new FormData();
        // Append files. The backend needs to know how to handle multiple files.
        // Common approach: send arrays like 'images[]' and 'metadata[]'
        for (let i = 0; i < imageFiles.length; i++) {
            formData.append('images[]', imageFiles[i], imageFiles[i].name);
        }
         for (let i = 0; i < metadataFiles.length; i++) {
             formData.append('metadata[]', metadataFiles[i], metadataFiles[i].name);
        }


        try {
            // Backend '/admin/upload.php' needs significant implementation for FormData handling
            const response = await uploadBulkData(formData);
            setSuccess(response.data.message || `Successfully processed ${ imageFiles.length } pairs.`); // Escaped: ${imageFiles.length}
            toast.success(response.data.message || `Successfully processed ${imageFiles.length} pairs.`);
            setImageFiles(null); // Clear selection
            setMetadataFiles(null);
            // Reset file input visually (trick)
            event.target.reset();
        } catch (err) {
            console.error("Upload error:", err);
            const errMsg = err.response?.data?.error || 'Upload failed. See console for details.';
            setError(errMsg);
            toast.error(`Upload failed: ${errMsg}`); // Escaped: ${errMsg}
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3, p: 2, border: '1px dashed grey', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>Bulk Upload Images & Metadata</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <Box mb={2}>
                <Typography variant="body1" gutterBottom>Select Image Files (.png, .jpg):</Typography>
                <Input
                    type="file"
                    inputProps={{ multiple: true, accept: "image/png, image/jpeg" }}
                    onChange={handleImageChange}
                    required
                    disabled={isLoading}
                />
                 {imageFiles && <Typography variant="caption">{imageFiles.length} image(s) selected</Typography>}
            </Box>

            <Box mb={2}>
                 <Typography variant="body1" gutterBottom>Select Metadata Files (.json):</Typography>
                 <Input
                    type="file"
                    inputProps={{ multiple: true, accept: "application/json" }} // Correct accept type
                    onChange={handleMetadataChange} // Correct handler
                    required
                    disabled={isLoading}
                    style={{ display: 'block', marginBottom: '8px' }} // Basic styling
                    id="image-upload-input"
                />
                 {metadataFiles && <Typography variant="caption">{metadataFiles.length} metadata file(s) selected</Typography>}
            </Box>

             <Typography variant="caption" display="block" sx={{mb: 2}}>
                Note: Ensure filenames match between images and metadata (e.g., building_0.png and building_0.json). The backend will perform the matching.
            </Typography>

            <Button type="submit" variant="contained" disabled={isLoading || !imageFiles || !metadataFiles}>
                {isLoading ? <CircularProgress size={24} /> : 'Upload Files'}
            </Button>
        </Box>
    );
};

export default BulkUpload;
